import { useMemo, useState } from "react";
import { calendarDateKey, eventsForCalendarDay, type FamilyCalendarEvent } from "../lib/calendarEvents";

type CalendarMember = { uid: string; firstName: string };
type CalendarScreenProps = {
  events: FamilyCalendarEvent[];
  members: CalendarMember[];
  onAdd: (dateKey?: string) => void;
  onEdit: (event: FamilyCalendarEvent) => void;
  onOpenConnections: () => void;
  familyPulse: { headline: string; body: string; source: string };
};
const WEEKDAYS = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];

function addDays(date: Date, amount: number) {
  const next = new Date(date);
  next.setDate(next.getDate() + amount);
  return next;
}
function eventTime(event: FamilyCalendarEvent) {
  if (!event.time) return "All day";
  const start = new Date(`2000-01-01T${event.time}`).toLocaleTimeString([], { hour: "numeric", minute: "2-digit" });
  if (!event.endTime) return start;
  const end = new Date(`2000-01-01T${event.endTime}`).toLocaleTimeString([], { hour: "numeric", minute: "2-digit" });
  return `${start}–${end}`;
}
function sourceLabel(event: FamilyCalendarEvent) {
  if (event.source === "google") return "Google";
  if (event.source === "apple") return "Apple";
  return "Family";
}
function EventRow({ event, members, onEdit }: { event: FamilyCalendarEvent; members: CalendarMember[]; onEdit: (event: FamilyCalendarEvent) => void; }) {
  const people = event.participantUids?.length
    ? members.filter((member) => event.participantUids?.includes(member.uid)).map((member) => member.firstName).join(", ")
    : "Whole family";
  return (
    <button type="button" className="calendar-agenda-row" onClick={() => onEdit(event)}>
      <span className="calendar-agenda-time">{eventTime(event)}</span>
      <span className="calendar-agenda-copy">
        <strong>{event.title}</strong>
        <small>{[event.location, people, sourceLabel(event)].filter(Boolean).join(" · ")}</small>
      </span>
      <span aria-hidden="true">›</span>
    </button>
  );
}

export function CalendarScreen({ events, members, onAdd, onEdit, onOpenConnections, familyPulse }: CalendarScreenProps) {
  const [view, setView] = useState<"agenda" | "month">("agenda");
  const [visibleMonth, setVisibleMonth] = useState(() => new Date(new Date().getFullYear(), new Date().getMonth(), 1));
  const [selectedMonthDay, setSelectedMonthDay] = useState<Date | null>(null);
  const today = useMemo(() => new Date(), []);
  const todayKey = calendarDateKey(today);
  const todayEvents = useMemo(() => eventsForCalendarDay(events, today), [events, today]);
  const weekDays = useMemo(() => Array.from({ length: 6 }, (_, index) => addDays(today, index + 1)), [today]);
  const monthDays = useMemo(() => {
    const first = new Date(visibleMonth.getFullYear(), visibleMonth.getMonth(), 1);
    const gridStart = addDays(first, -first.getDay());
    return Array.from({ length: 42 }, (_, index) => addDays(gridStart, index));
  }, [visibleMonth]);

  return (
    <section className="panel calendar-page" aria-labelledby="calendar-heading" data-home-hunt-view={`Calendar:${view}`}>
      <header className="calendar-page-header">
        <div><p className="eyebrow">Family calendar</p><h1 id="calendar-heading">What’s happening</h1><p>Everyone’s plans in one clear place.</p></div>
        <button type="button" className="primary-action" onClick={() => onAdd(todayKey)}>+ Add event</button>
      </header>
      <section className="calendar-daily-pulse" aria-label="Family Pulse">
        <div className="calendar-pulse-mark" aria-hidden="true">☀</div>
        <div><p className="leader-label">Today’s family pulse</p><h2>{familyPulse.headline}</h2><p>{familyPulse.body}</p></div>
        <span>{familyPulse.source}</span>
      </section>
      <nav className="calendar-view-tabs" aria-label="Calendar view">
        <button type="button" className={view === "agenda" ? "active" : ""} aria-current={view === "agenda" ? "page" : undefined} onClick={() => setView("agenda")}>Today & this week</button>
        <button type="button" className={view === "month" ? "active" : ""} aria-current={view === "month" ? "page" : undefined} onClick={() => setView("month")}>Month</button>
        <button type="button" onClick={onOpenConnections}>Connections</button>
      </nav>

      {view === "agenda" ? (
        <div className="calendar-agenda">
          <section className="calendar-agenda-section calendar-today-section">
            <header>
              <div><p className="leader-label">Today</p><h2>{today.toLocaleDateString([], { weekday: "long", month: "long", day: "numeric" })}</h2></div>
              <button type="button" aria-label="Add an event today" onClick={() => onAdd(todayKey)}>+</button>
            </header>
            {todayEvents.length ? <div className="calendar-agenda-list">{todayEvents.map((event) => <EventRow key={event.id} event={event} members={members} onEdit={onEdit} />)}</div> : (
              <div className="calendar-empty-state"><strong>Nothing planned today.</strong><span>Enjoy the open space or add something for the family.</span><button type="button" onClick={() => onAdd(todayKey)}>+ Add an event</button></div>
            )}
          </section>
          <section className="calendar-agenda-section">
            <header><div><p className="leader-label">Coming up</p><h2>This week</h2></div></header>
            <div className="calendar-week-list">
              {weekDays.map((day) => {
                const dayEvents = eventsForCalendarDay(events, day);
                return (
                  <section className="calendar-week-day" key={calendarDateKey(day)}>
                    <div className="calendar-week-date"><strong>{day.toLocaleDateString([], { weekday: "short" })}</strong><span>{day.toLocaleDateString([], { month: "short", day: "numeric" })}</span></div>
                    <div>{dayEvents.length ? dayEvents.map((event) => <EventRow key={event.id} event={event} members={members} onEdit={onEdit} />) : <button type="button" className="calendar-open-day" onClick={() => onAdd(calendarDateKey(day))}>Open day · Add event</button>}</div>
                  </section>
                );
              })}
            </div>
          </section>
        </div>
      ) : (
        <div className="calendar-month">
          <div className="calendar-month-toolbar">
            <button type="button" aria-label="Previous month" onClick={() => setVisibleMonth((month) => new Date(month.getFullYear(), month.getMonth() - 1, 1))}>‹</button>
            <h2>{visibleMonth.toLocaleDateString([], { month: "long", year: "numeric" })}</h2>
            <button type="button" aria-label="Next month" onClick={() => setVisibleMonth((month) => new Date(month.getFullYear(), month.getMonth() + 1, 1))}>›</button>
          </div>
          <div className="calendar-weekdays" aria-hidden="true">{WEEKDAYS.map((weekday) => <span key={weekday}>{weekday}</span>)}</div>
          <div className="calendar-month-grid">
            {monthDays.map((day) => {
              const dayEvents = eventsForCalendarDay(events, day);
              const key = calendarDateKey(day);
              return (
                <button type="button" className={["calendar-day", day.getMonth() !== visibleMonth.getMonth() ? "outside-month" : "", key === todayKey ? "today" : "", selectedMonthDay && calendarDateKey(selectedMonthDay) === key ? "selected" : ""].filter(Boolean).join(" ")} key={key} onClick={() => setSelectedMonthDay(day)} aria-label={`${day.toLocaleDateString([], { month: "long", day: "numeric" })}: ${dayEvents.length} events`}>
                  <span className="calendar-day-number">{day.getDate()}</span>
                  <span className="calendar-day-events">{dayEvents.slice(0, 2).map((event) => <span className="calendar-event" key={event.id}>{event.title}</span>)}{dayEvents.length > 2 && <span className="calendar-more">+{dayEvents.length - 2}</span>}</span>
                </button>
              );
            })}
          </div>
          {selectedMonthDay && <section className="calendar-month-day-panel">
            <header><div><p className="leader-label">Selected day</p><h3>{selectedMonthDay.toLocaleDateString([], { weekday: "long", month: "long", day: "numeric" })}</h3></div><button type="button" onClick={() => onAdd(calendarDateKey(selectedMonthDay))}>+ Add</button></header>
            {eventsForCalendarDay(events, selectedMonthDay).length
              ? eventsForCalendarDay(events, selectedMonthDay).map((event) => <EventRow key={event.id} event={event} members={members} onEdit={onEdit} />)
              : <p>Nothing planned for this day.</p>}
          </section>}
          <p className="calendar-month-help">Choose a date to see its plans or add an event.</p>
        </div>
      )}
    </section>
  );
}
